import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import SpecialsBlock from '../components/SpecialsBlock';
import { DEFAULT_SPECIALS, cloneSpecials, normalizeSpecials, cleanSpecials } from '../menuDefaults';
import { formatSavedAt } from './dateUtils';

const SPECIALS_REF = () => doc(db, 'settings', 'specials');

function SpecialsEditor({ onDirtyChange }) {
  const [draft, setDraft] = useState(null);
  const [savedSnap, setSavedSnap] = useState('');
  const [savedAt, setSavedAt] = useState('');
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDoc(SPECIALS_REF())
      .then((snap) => {
        if (cancelled) return;
        const data = snap.exists() ? snap.data() : null;
        const specials = data ? normalizeSpecials(data) : cloneSpecials(DEFAULT_SPECIALS);
        setDraft(specials);
        setSavedSnap(JSON.stringify(specials));
        setSavedAt(data && data.updatedAt ? data.updatedAt : '');
      })
      .catch((err) => {
        console.error(err);
        if (cancelled) return;
        const specials = cloneSpecials(DEFAULT_SPECIALS);
        setDraft(specials);
        setSavedSnap(JSON.stringify(specials));
        setStatus('Could not load the saved specials — showing the defaults.');
      });
    return () => { cancelled = true; };
  }, []);

  const dirty = !!draft && JSON.stringify(draft) !== savedSnap;
  useEffect(() => { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  const update = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  const updateItem = (i, field, value) => {
    setDraft((prev) => ({
      ...prev,
      items: prev.items.map((it, idx) => (idx === i ? { ...it, [field]: value } : it)),
    }));
  };

  const addItem = () => {
    setDraft((prev) => ({ ...prev, items: [...prev.items, { name: '', price: '', description: '' }] }));
  };

  const removeItem = (i) => {
    setDraft((prev) => ({ ...prev, items: prev.items.filter((_, idx) => idx !== i) }));
  };

  const moveItem = (i, delta) => {
    setDraft((prev) => {
      const j = i + delta;
      if (j < 0 || j >= prev.items.length) return prev;
      const items = [...prev.items];
      [items[i], items[j]] = [items[j], items[i]];
      return { ...prev, items };
    });
  };

  const save = async () => {
    setSaving(true);
    setStatus('');
    try {
      const clean = cleanSpecials(draft);
      const updatedAt = new Date().toISOString();
      await setDoc(SPECIALS_REF(), { ...clean, updatedAt });
      setDraft(clean);
      setSavedSnap(JSON.stringify(clean));
      setSavedAt(updatedAt);
      setStatus('Saved! The menu is live on the site.');
    } catch (err) {
      console.error(err);
      setStatus('Save failed — check your connection and try again.');
    }
    setSaving(false);
  };

  const discard = () => {
    setDraft(JSON.parse(savedSnap));
    setStatus('');
  };

  if (!draft) {
    return <div className="schedule-loading"><div className="schedule-spinner" /></div>;
  }

  return (
    <>
      <div className="admin-card">
        <h3>MONTHLY SPECIALS</h3>
        <p className="admin-help">
          This is the specials section at the top of the menu page. Change the title each month,
          add or remove drinks, then tap Save — it goes live instantly.
        </p>

        <label className="admin-field">
          Section title
          <input
            type="text"
            value={draft.heading}
            placeholder="e.g. NOVEMBER SPECIALS"
            onChange={(e) => update({ heading: e.target.value })}
          />
        </label>

        <div className="admin-subhead">Special drinks</div>
        {draft.items.length === 0 && (
          <p className="admin-help">No special drinks yet — tap &quot;Add a drink&quot; below.</p>
        )}
        {draft.items.map((it, i) => (
          <div key={i} className="admin-item-row">
            <div className="admin-item-main">
              <input
                type="text"
                placeholder="Drink name"
                value={it.name}
                onChange={(e) => updateItem(i, 'name', e.target.value)}
              />
              <input
                type="text"
                className="admin-price-input"
                placeholder="$7.75"
                value={it.price}
                onChange={(e) => updateItem(i, 'price', e.target.value)}
              />
            </div>
            <input
              type="text"
              placeholder="Short description (optional)"
              value={it.description}
              onChange={(e) => updateItem(i, 'description', e.target.value)}
            />
            <div className="admin-item-actions">
              <button type="button" className="admin-icon-btn" onClick={() => moveItem(i, -1)} disabled={i === 0} aria-label="Move up">▲</button>
              <button type="button" className="admin-icon-btn" onClick={() => moveItem(i, 1)} disabled={i === draft.items.length - 1} aria-label="Move down">▼</button>
              <button type="button" className="admin-remove-btn" onClick={() => removeItem(i)}>✕ Remove</button>
            </div>
          </div>
        ))}
        <button type="button" className="admin-add-stop-btn" onClick={addItem}>+ Add a drink</button>

        <div className="admin-subhead">Special cloud topping</div>
        <p className="admin-help">
          Shows as the last card in the specials and under &quot;Cloud Topping&quot; in Add Ons.
          Leave the name blank if there&apos;s no special topping this month.
        </p>
        <div className="admin-item-main">
          <input
            type="text"
            placeholder="e.g. Cinnamon Sugar Cloud"
            value={draft.cloudTopping.name}
            onChange={(e) => update({ cloudTopping: { ...draft.cloudTopping, name: e.target.value } })}
          />
          <input
            type="text"
            className="admin-price-input"
            placeholder="$1.50"
            value={draft.cloudTopping.price}
            onChange={(e) => update({ cloudTopping: { ...draft.cloudTopping, price: e.target.value } })}
          />
        </div>

        <button className="cta-button admin-full-btn" onClick={save} disabled={saving || !dirty}>
          {saving ? 'Saving...' : dirty ? 'Save Specials' : 'Saved'}
        </button>
        {dirty && (
          <p className="admin-unsaved">
            Unsaved changes · <button type="button" className="admin-link-btn admin-link-sm" onClick={discard}>Undo my changes</button>
          </p>
        )}
        {status && <p className="admin-status">{status}</p>}
        {savedAt && !dirty && <p className="admin-meta">Last saved {formatSavedAt(savedAt)}</p>}
      </div>

      <div className="admin-card admin-preview">
        <div className="admin-subhead admin-subhead-center">Preview — this is how it will look on the menu</div>
        <div className="admin-preview-menu">
          <SpecialsBlock specials={draft} />
          {cleanSpecials(draft).items.length === 0 && !cleanSpecials(draft).cloudTopping.name && (
            <p className="admin-help">Nothing to show yet.</p>
          )}
        </div>
      </div>
    </>
  );
}

export default SpecialsEditor;
